import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import cn from 'classnames';

import BaseModal from '@/components/modal/lib/BaseModal';
import { useLoggedInUser } from '@/components/hook/user';
import CMSResponsiveImage from '@/components/common/CMSResponsiveImage/CMSResponsiveImage';
import Text from '@/components/common/Text/Text';
import Button from '@/components/common/Button/Button';

import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

import { USER_STATUS } from '@/types/User';
import { Perk } from '@/types/Perk';
import { copyToClipboard } from '@/util/clipboard';
import { isPriveeMember } from '@/util/user_client';

import './PerkModal.style.scss';
import { openUrlIOSDeepLink } from '@/util/iosDeepLinks';

type PerkModalProps = {
  className?: string;
  onClose: () => void;
  onLoginLinkClick: (perk: Perk) => void;
  onApplyLinkClick: () => void;
  data: Perk;
};

function PerkModal(props: PerkModalProps) {
  const {
    className = '',
    onClose,
    onLoginLinkClick,
    onApplyLinkClick,
    data
  } = props;

  const [promoRevealed, setPromoRevealed] = useState<boolean>(false);
  const [promoCopied, setPromoCopied] = useState<boolean>(false);

  const user = useLoggedInUser();
  const { isIOSWebView } = useContextBroker();
  const pathname = usePathname();

  const isPriveePerksPath = pathname === '/perks-reserve';
  const hasPriveeAccess = isPriveeMember(user);

  const {
    description,
    descriptionButtonText,
    logo,
    promoButtonText,
    promoButtonLink,
    promoText,
    promoCode,
    termsAndConditions
  } = data;

  let promoUrl: string;

  if (isIOSWebView) {
    promoUrl = openUrlIOSDeepLink(promoButtonLink);
  } else {
    promoUrl = promoButtonLink;
  }

  const handlePromoRevealClick = () => {
    setPromoRevealed(true);
  };

  const handlePromoCopyClick = () => {
    if (promoCode && !promoCopied) {
      copyToClipboard(promoCode);
      setPromoCopied(true);

      setTimeout(() => {
        setPromoCopied(false);
      }, 2000);
    }
  };

  return (
    <BaseModal
      className={cn('PerkModal', className)}
      onClose={onClose}
    >
      <div className='PerkModal-container'>
        <CMSResponsiveImage
          className='PerkModal-logo'
          data={logo}
        />
        <Text
          className='PerkModal-title'
          variant='title-xl'
          italic
        >
          {data.name}
        </Text>
        <Text
          variant='text-m'
          className='PerkModal-description'
        >
          {description}
        </Text>
      </div>

      <div className='PerkModal-body'>
        {/* LOGGED OUT VIEW */}
        {user === undefined && (
          <div className='PerkModal-dynamic PerkModal-dynamic-loggedout'>
            <Text variant='span-s'>
              <Text
                className='PerkModal-authLink PerkModal-authlink-login cursor'
                variant='span-s'
                onClick={() => {
                  onLoginLinkClick(data);
                }}
              >
                Login
              </Text>{' '}
              or{' '}
              <Text
                className='PerkModal-authLink PerkModal-authLink-apply cursor'
                variant='span-s'
                onClick={() => {
                  onApplyLinkClick?.();
                }}
              >
                Apply for Membership
              </Text>{' '}
              to reveal the perk
            </Text>
          </div>
        )}

        {/* USER PENDING VIEW */}
        {(user?.status === USER_STATUS.PENDING ||
          user?.status === USER_STATUS.PENDING_MORE_INFO ||
          user?.status === USER_STATUS.WAITLIST) && (
          <div className='PerkModal-dynamic PerkModal-dynamic-pending'>
            <Text
              variant='text-s'
              className='PerkModal-dynamic-error-text'
            >
              Your membership status is pending. Access to perks unlocks once
              your membership is active.
            </Text>
          </div>
        )}

        {(user?.status === USER_STATUS.CANCELLED ||
          user?.status === USER_STATUS.FROZEN) && (
          <div className='PerkModal-dynamic PerkModal-dynamic-cancelled'>
            <Text
              variant='text-s'
              className='PerkModal-dynamic-error-text'
            >
              Reactivate your membership to unlock access to perks.
            </Text>
          </div>
        )}

        {/* ACTIVE VIEW */}
        {user?.status === USER_STATUS.ACTIVE &&
          (isPriveePerksPath && !hasPriveeAccess ? (
            <div className='PerkModal-dynamic PerkModal-dynamic-privee-gated'>
              <Text
                variant='text-s'
                className='PerkModal-dynamic-privee-gated-text'
              >
                This perk is exclusive to Réservé members.
                <br />
                Learn more about Réservé{' '}
                <Link
                  className='PerkModal-privee-gated-link'
                  href='/reserve'
                  onClick={onClose}
                >
                  here
                </Link>
                .
              </Text>
            </div>
          ) : (
            <div className='PerkModal-dynamic PerkModal-dynamic-active'>
              {/* USER CLICKED PROMO VIEW */}
              {promoRevealed ? (
                <>
                  {/* Onclick of button, it will open the promo link in a new tab */}
                  <Button
                    className='PerkModal-dynamic-description-button'
                    variant='light'
                    size='small'
                    onClick={() => {
                      window.open(promoUrl, '_blank');
                    }}
                  >
                    {promoButtonText}
                  </Button>
                  {promoCode && (
                    <div className='PerkModal-dynamic-promoCode-container'>
                      <Text
                        variant='span-m'
                        className='PerkModal-dynamic-promoCode-copy-text cursor'
                        onClick={handlePromoCopyClick}
                      >
                        {`COPY PROMO CODE: ${promoCode}`}
                      </Text>
                      {promoCopied && (
                        <Text
                          variant='text-s'
                          className='PerkModal-dynamic-promoCode-success'
                        >
                          Success! Code copied!
                        </Text>
                      )}
                    </div>
                  )}
                  <Text
                    className='PerkModal-dynamic-promoText'
                    variant='title-xs'
                  >
                    {promoText}
                  </Text>

                  {termsAndConditions && (
                    <Text
                      className='PerkModal-dynamic-terms'
                      variant='text-s'
                    >
                      {termsAndConditions}
                    </Text>
                  )}
                </>
              ) : (
                <Button
                  className='PerkModal-dynamic-description-button'
                  onClick={handlePromoRevealClick}
                  variant='light'
                  size='small'
                >
                  {descriptionButtonText}
                </Button>
              )}
            </div>
          ))}
      </div>
    </BaseModal>
  );
}

export default PerkModal;
